import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { Table } from '@tanstack/react-table'
import { useTranslation } from '@/hooks/use-translation'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface DataTablePaginationProps<TData> {
  table: Table<TData>
  currentPage: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  pageSizeOptions?: number[]
  showRowsSelected?: boolean
  showCurrentRange?: boolean
  showPageInfo?: boolean
  itemsDisplayed?: number
  hasNextPage?: boolean
}

export function DataTablePagination<TData>({
  table,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
  showRowsSelected = false,
  showCurrentRange = true,
  showPageInfo = true,
  itemsDisplayed,
  hasNextPage,
}: DataTablePaginationProps<TData>) {
  const { t } = useTranslation()
  const skip = (currentPage - 1) * pageSize
  const displayedItems = itemsDisplayed ?? table.getRowModel().rows.length
  const canPreviousPage = currentPage > 1
  const canNextPage =
    hasNextPage !== undefined ? hasNextPage : displayedItems >= pageSize

  const handlePageSizeChange = (value: string) => {
    onPageSizeChange(Number(value))
    onPageChange(1) // Reset to first page when page size changes
  }

  return (
    <div className='flex items-center justify-between space-x-2 py-4'>
      {/* Current range display */}
      {showCurrentRange && (
        <div className='text-sm text-muted-foreground'>
          {t('table.showing')} {skip + 1} {t('table.to')}{' '}
          {skip + displayedItems} {t('table.of')} {skip + displayedItems}
          {canNextPage ? '+' : ''} {t('table.items')}
        </div>
      )}

      {/* Rows selected display */}
      {showRowsSelected && (
        <div className='text-sm text-muted-foreground'>
          {table.getFilteredSelectedRowModel().rows.length} {t('table.of')}{' '}
          {table.getFilteredRowModel().rows.length} {t('table.rowsSelected')}
        </div>
      )}

      {/* Page size selector and navigation */}
      <div className='flex items-center space-x-4'>
        <div className='flex items-center space-x-2'>
          <Label htmlFor='page-size'>{t('table.show')}</Label>
          <Select
            value={pageSize.toString()}
            onValueChange={handlePageSizeChange}
          >
            <SelectTrigger id='page-size' className='w-[100px]'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((option) => (
                <SelectItem key={option} value={option.toString()}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showPageInfo && (
          <div className='flex items-center space-x-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => onPageChange(currentPage - 1)}
              disabled={!canPreviousPage}
            >
              <ChevronLeftIcon className='mr-2 h-4 w-4' />
              {t('table.previous')}
            </Button>
            <div className='flex items-center space-x-1'>
              <span className='text-sm'>
                {t('table.page')} {currentPage}
              </span>
            </div>
            <Button
              variant='outline'
              size='sm'
              onClick={() => onPageChange(currentPage + 1)}
              disabled={!canNextPage}
            >
              {t('table.next')}
              <ChevronRightIcon className='ml-2 h-4 w-4' />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
